/**
 * Codex PR #784 round-11 P1 (`scene/SceneBulkLoad.ts:1391`) — PENDING TEXT
 * RETRY REGISTRY.
 *
 * # The gap this closes
 *
 * Round 10 made a text run that the PR-V5 scale gate produced NOTHING for
 * RELEASE its `EntityRender.textEmit` claim instead of marking it emitted, so
 * the run stays ELIGIBLE for a later, larger-scale build
 * (`sceneBulkLoadHelpers.ts emitTextMeshes`). Eligibility alone never rebuilt
 * anything: the only production caller of `emitReliftDirectMeshes` is the
 * on-demand resolver inside `SceneBulkLoad.ts finishSceneBulkLoad`, and
 * `scene/FrameLoops.tsx` Pass 2 skips that resolver for every id already in
 * `liftedEntityIdsRef`. So when an INSERT is lifted because a large child
 * crosses the LOD threshold while a smaller TEXT / MTEXT satellite is still
 * gated out, further zooming leaves the parent lifted, the released claim is
 * never reconsidered, and the label stays absent until a cull → relift cycle
 * or the next bulk load.
 *
 * # The shape of the fix
 *
 * `emitTextMeshes` REGISTERS every run it could not build, together with the
 * scale it was attempted at and the id whose lift owns it; `FrameLoops` drains
 * the registry with {@link retryPendingTextRuns} on the frames where the scale
 * that gates them actually moved. Nothing walks the lifted set, and nothing
 * re-resolves an entity: the retry re-submits the run's OWN feed renderable
 * through the same `emitTextMeshes` seam, so the work is O(skipped runs) and
 * the claim/settle contract stays the single truth about what exists.
 *
 * # Why it hangs off `entityRenders`
 *
 * The registry is per-SCENE state that both the emitter (which has the pass's
 * scene / font map / build token) and the frame loop (which has the camera)
 * must reach, and neither owns a ref the other can see. It is therefore keyed
 * — in a module-level `WeakMap` — by the one per-scene object both already
 * hold: `SceneBulkLoadRefs.entityRendersRef.current`, the map whose
 * `textEmit` state this registry is the index for. Weak keying means a
 * discarded scene's registry is collected with it; a REUSED map (the registries
 * are cleared in place, not replaced) is emptied explicitly by
 * {@link clearPendingTextRetries}, which `SceneBulkLoad.ts` calls wherever it
 * bumps the text build token — the exact moment every in-flight and pending
 * build of the previous pass becomes invalid.
 *
 * # Cost
 *
 *   * register / release — O(1) per text run, on the emit path that already
 *     touched that run.
 *   * drain — O(1) when no id can have been unblocked (`ceiling <= minScale`,
 *     the smallest scale any pending run was skipped at), else O(pending runs).
 *     `minScale` is a conservative LOWER BOUND maintained without rescans:
 *     registration lowers it, release never raises it, and each full walk
 *     recomputes it exactly from the survivors.
 */

import type { RenderableEntity } from "../types";
import type { EntityRender } from "./SceneRefsTypes";

/** One text run that produced no mesh, waiting for a larger scale. */
interface PendingTextRun {
  /**
   * The renderable to RE-SUBMIT — the run's own feed entity for a real TEXT /
   * MTEXT (or a block-content text child), and the PARENT for a synthesised
   * DIMENSION / MULTILEADER label, because `emitTextMeshes` synthesises those
   * labels from the parent and routes their meshes onto the parent's
   * `satelliteTextObjects`. Re-submitting the label itself would build a mesh
   * with the wrong owner, so the source is what is stored.
   */
  source: RenderableEntity;
  /**
   * The SPATIAL-INDEX id whose lift owns this run (`renderParentId` when the
   * source is a satellite, else its own id). This is the id a viewport names in
   * `ViewportLiftRequests.lift`, so it is the id the per-viewport scale must be
   * looked up under.
   */
  ownerId: string;
  /** The `v5Zoom` the build was attempted — and produced nothing — at. */
  scale: number;
}

/** Re-run one emit with the pass context the runs were registered from. */
type TextEmitFn = (sources: RenderableEntity[], v5Zoom: number) => void;

interface PendingTextRetryRegistry {
  runs: Map<string, PendingTextRun>;
  /** Lower bound on `min(runs[].scale)`. See the module doc. */
  minScale: number;
  emit: TextEmitFn | null;
}

const REGISTRIES = new WeakMap<
  Map<string, EntityRender>,
  PendingTextRetryRegistry
>();

function registryFor(
  entityRenders: Map<string, EntityRender>,
): PendingTextRetryRegistry {
  let registry = REGISTRIES.get(entityRenders);
  if (!registry) {
    registry = {
      runs: new Map<string, PendingTextRun>(),
      minScale: Number.POSITIVE_INFINITY,
      emit: null,
    };
    REGISTRIES.set(entityRenders, registry);
  }
  return registry;
}

/**
 * Record that `runId` produced no mesh at `scale`, so a later frame at a larger
 * scale re-attempts it. `emit` is the caller's own re-entry point (its scene,
 * entity registry, build token, font map and hidden-layer ref already bound);
 * the newest one wins, which is correct because a later pass's context is the
 * live one.
 */
export function registerPendingTextRun(
  entityRenders: Map<string, EntityRender>,
  runId: string,
  run: { source: RenderableEntity; ownerId: string; scale: number },
  emit: TextEmitFn,
): void {
  const registry = registryFor(entityRenders);
  registry.runs.set(runId, run);
  if (run.scale < registry.minScale) registry.minScale = run.scale;
  registry.emit = emit;
}

/**
 * Drop `runId` from the pending set — called when a build is CLAIMED (it is
 * being attempted right now; a skip re-registers it with the new scale) and so
 * also covers the success path, where nothing re-registers.
 */
export function releasePendingTextRun(
  entityRenders: Map<string, EntityRender>,
  runId: string,
): void {
  REGISTRIES.get(entityRenders)?.runs.delete(runId);
}

/** Drop every pending run (a new bulk load / teardown invalidates them all). */
export function clearPendingTextRetries(
  entityRenders: Map<string, EntityRender>,
): void {
  const registry = REGISTRIES.get(entityRenders);
  if (!registry) return;
  registry.runs.clear();
  registry.minScale = Number.POSITIVE_INFINITY;
  registry.emit = null;
}

/** Pending-run count — diagnostics and tests. */
export function pendingTextRunCount(
  entityRenders: Map<string, EntityRender>,
): number {
  return REGISTRIES.get(entityRenders)?.runs.size ?? 0;
}

/**
 * Re-attempt every pending run whose governing scale has grown since it was
 * skipped, and return how many were re-submitted.
 *
 * The governing scale of a run is `max(mainScale, viewportScales[ownerId])` —
 * the largest scale that will actually render it this frame, which is the same
 * number the lift itself builds against (`EntityLiftRequest.emitScale`). Runs
 * are grouped by that scale so each `emit` call carries ONE `v5Zoom`, exactly
 * as a bulk-load or relift emit does; in practice that is one or two groups.
 *
 * Re-submitting is not re-emitting: `emitTextMeshes`'s claim gate drops any run
 * that already has a mesh or a build in flight, so a source shared by several
 * runs (a DIMENSION with two labels, one of them emitted) rebuilds only what is
 * still missing.
 */
export function retryPendingTextRuns(args: {
  entityRenders: Map<string, EntityRender>;
  /** Live main-camera zoom (CSS px per world unit). */
  mainScale: number;
  /** `ViewportLiftRequests.lift` — per-owner viewport scales, if any. */
  viewportScales?: ReadonlyMap<string, number> | undefined;
  /** `ViewportLiftRequests.maxLiftScale` — the O(1) ceiling. */
  maxViewportScale?: number | undefined;
}): number {
  const registry = REGISTRIES.get(args.entityRenders);
  if (!registry || registry.runs.size === 0) return 0;
  const emit = registry.emit;
  if (!emit) return 0;
  const ceiling = Math.max(args.mainScale, args.maxViewportScale ?? 0);
  // Nothing can have been unblocked: no per-run governing scale can exceed the
  // ceiling, and no pending run was skipped below `minScale`.
  if (!(ceiling > registry.minScale)) return 0;

  const byScale = new Map<number, Map<string, RenderableEntity>>();
  let retried = 0;
  let survivingMin = Number.POSITIVE_INFINITY;
  for (const run of registry.runs.values()) {
    const scale = Math.max(
      args.mainScale,
      args.viewportScales?.get(run.ownerId) ?? 0,
    );
    if (!(scale > run.scale)) {
      if (run.scale < survivingMin) survivingMin = run.scale;
      continue;
    }
    let group = byScale.get(scale);
    if (!group) {
      group = new Map<string, RenderableEntity>();
      byScale.set(scale, group);
    }
    // The claim inside `emitTextMeshes` releases this entry synchronously; a
    // second skip re-registers it with the scale just attempted.
    group.set(run.source.id, run.source);
    retried += 1;
  }
  registry.minScale = survivingMin;
  for (const [scale, group] of byScale) {
    emit([...group.values()], scale);
  }
  return retried;
}
